import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { motion } from "framer-motion";
import { fadeIn, slideInFromBottom, staggerContainer } from "@/lib/animations";
import { Check } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { usePremium } from "@/hooks/use-premium";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

interface PricingTierProps {
  title: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  onButtonClick: () => void;
  popular?: boolean;
  disabled?: boolean;
}

function PricingTier({
  title,
  price,
  description,
  features,
  buttonText,
  onButtonClick,
  popular = false,
  disabled = false,
}: PricingTierProps) {
  return (
    <motion.div
      variants={slideInFromBottom}
      className={`bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 relative ${
        popular
          ? "border-2 border-pink-500 transform scale-105 z-10"
          : "border border-gray-200"
      }`}
      whileHover={{
        scale: popular ? 1.08 : 1.05,
        transition: { duration: 0.2 },
      }}
    >
      {popular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-pink-500 text-white text-xs uppercase font-bold tracking-wider py-1 px-4 rounded-full">
          Most Popular
        </div>
      )}
      <h3 className="text-xl font-semibold mb-2 text-center">{title}</h3>
      <p className="text-3xl font-bold text-center text-gray-900 mb-2">
        {price}
      </p>
      <p className="text-gray-600 text-center text-sm mb-6">{description}</p>
      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className="h-5 w-5 text-pink-500 mr-2 shrink-0 mt-0.5" />
            <span className="text-gray-600 text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      <Button
        className={`w-full ${
          popular
            ? "bg-pink-600 hover:bg-pink-700 text-white"
            : "bg-white text-pink-600 border border-pink-200 hover:bg-pink-50"
        } ${disabled ? "opacity-70 cursor-not-allowed" : ""} ${
          buttonText === "Already Premium"
            ? "bg-green-600 hover:bg-green-700"
            : ""
        }`}
        onClick={onButtonClick}
        disabled={disabled || buttonText === "Already Premium"}
      >
        {buttonText === "Already Premium" ? (
          <span className="flex items-center justify-center">
            <Check className="h-4 w-4 mr-2" /> {buttonText}
          </span>
        ) : (
          buttonText
        )}
      </Button>
    </motion.div>
  );
}

export default function Pricing() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { isPremium, loading, startCheckout } = usePremium();
  const [isProcessing, setIsProcessing] = useState(false);

  const { toast } = useToast();

  // Check for upgraded=true query parameter to show success message
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("upgraded") === "true") {
      toast({
        title: "Upgrade Successful!",
        description:
          "Thank you for upgrading to Premium! You now have access to unlimited quiz responses.",
        variant: "default",
      });

      // Remove the query parameter from the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast, location]);

  const handleFreeTierClick = () => {
    if (user) {
      setLocation("/create-quiz");
    } else {
      setLocation("/auth");
    }
  };

  const handleProTierClick = async () => {
    if (!user) {
      // Redirect to auth if user is not logged in
      setLocation("/auth");
      return;
    }

    if (isPremium) {
      // If already premium, just go to create quiz
      toast({
        title: "Already Premium",
        description:
          "You already have a premium account. Enjoy creating unlimited quizzes!",
        variant: "default",
      });
      setLocation("/create-quiz");
      return;
    }

    try {
      setIsProcessing(true);
      // Launch Paddle checkout
      await startCheckout();
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Checkout Error",
        description:
          "There was a problem starting the checkout process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Layout>
      <motion.div
        className="py-16 md:py-24"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            variants={staggerContainer}
          >
            <motion.h1
              variants={slideInFromBottom}
              className="text-4xl md:text-5xl font-serif text-gray-900 mb-6"
            >
              Simple, Transparent Pricing
            </motion.h1>
            <motion.p
              variants={slideInFromBottom}
              className="text-lg text-gray-600 mb-8"
            >
              Choose the perfect plan for your wedding quiz needs. All plans
              include beautiful themes and an engaging quiz experience.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            variants={staggerContainer}
          >
            {/* Free Tier */}
            <PricingTier
              title="Free"
              price="€0"
              description="Perfect for small weddings"
              features={[
                "Create up to three quizzes",
                "Up to 50 quiz responses",
                "Custom themes",
                "Basic analytics",
                // "Share via unique link",
              ]}
              buttonText={user ? "Create Quiz" : "Get Started"}
              onButtonClick={handleFreeTierClick}
            />

            {/* Pro Tier */}
            <PricingTier
              title="Premium"
              price="€50"
              description="One-time payment"
              features={[
                "Everything in Free",
                "Unlimited quiz responses",
                "Priority support",
                "Advanced analytics",
                // "No branding",
              ]}
              buttonText={
                user
                  ? isProcessing
                    ? "Processing..."
                    : isPremium
                      ? "Already Premium"
                      : "Upgrade Now"
                  : "Sign Up"
              }
              onButtonClick={handleProTierClick}
              popular={true}
              disabled={isProcessing || loading}
            />

            {/* Enterprise Tier */}
            <PricingTier
              title="Enterprise"
              price="Contact Us"
              description="For large events and businesses"
              features={[
                "Everything in Premium",
                "Create unlimited quizzes",
                "Dedicated support",
                "Custom features & branding",
              ]}
              buttonText="Contact Sales"
              onButtonClick={() =>
                (window.location.href = "mailto:hello@vowquiz.com")
              }
            />
          </motion.div>

          <motion.div
            variants={slideInFromBottom}
            className="mt-16 text-center max-w-2xl mx-auto bg-pink-50 rounded-lg p-8"
          >
            <h3 className="font-serif text-2xl mb-4 text-gray-800">
              Need Something Custom?
            </h3>
            <p className="text-gray-600 mb-6">
              We offer custom solutions for wedding planners, venues, and large
              events. Contact us to discuss your specific requirements.
            </p>
            <Button
              variant="outline"
              className="border-pink-300 text-pink-700 hover:bg-pink-100"
              onClick={() =>
                (window.location.href = "mailto:hello@vowquiz.com")
              }
            >
              Contact Our Team
            </Button>
          </motion.div>

          <motion.div
            variants={slideInFromBottom}
            className="mt-16 text-center text-sm text-gray-500"
          >
            <p>
              All prices are in Euros (€). The Premium plan is a one-time
              payment that provides unlimited responses for your quizzes.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
}
